import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatStoreService } from '../shared/chat-store.service';

import { ContactListComponent } from './ui/contact-list.component';
import { ChatHeaderComponent } from './ui/chat-header.component';
import { ChatBodyComponent } from './ui/chat-body.component';
import { MessageComposerComponent } from './ui/message-composer.component';

@Component({
  standalone: true,
  selector: 'app-inbox',
  imports: [CommonModule, ContactListComponent, ChatHeaderComponent, ChatBodyComponent, MessageComposerComponent],
  template: `
  <div class="app">
    <aside class="sidebar" [class.hidden]="mobileChatOpen">
      <div class="sbTop">
        <div class="search">
          <span class="sIcon">🔎</span>
          <input
            [value]="store.search()"
            (input)="store.search.set(($any($event.target)).value)"
            placeholder="Search by name, number, or label"
            aria-label="Search" />
        </div>
        <div class="quickFilters" aria-label="Quick label filters">
          <button
            type="button"
            class="chip"
            *ngFor="let f of store.quickFilters()"
            [class.active]="store.activeLabelFilter()===f.key"
            [class.lead]="f.key==='Lead'"
            [class.sales]="f.key==='Sales'"
            [class.humanRequired]="f.key==='HumanRequired'"
            (click)="store.setActiveLabelFilter(f.key)">
            {{ f.label }} <span class="count">{{ f.count }}</span>
          </button>
        </div>
      </div>

      <app-contact-list
        [contacts]="store.contacts()"
        [selectedId]="store.selectedConversationId()"
        (select)="onSelect($event)">
      </app-contact-list>
    </aside>

    <main class="chat" *ngIf="store.conversation() as conv">
      <app-chat-header
        [contact]="store.selectedContact()"
        (back)="onBack()"
        (action)="onHeaderAction($event)"
        (searchQueryChange)="onChatSearch($event)"
        (toggleBot)="onToggleBot($event)"
        (toggleHumanRequired)="onToggleHumanRequired($event)">
      </app-chat-header>

      <app-chat-body
        [conversation]="conv"
        [messages]="store.messages()"
        [searchQuery]="chatSearchQuery">
      </app-chat-body>

      <app-message-composer (send)="store.sendMessage($event)"></app-message-composer>
    </main>
  </div>
  `,
  styles: [`
    :host{display:block;height:100%}
    .app{height:100%;display:flex;background:var(--bg)}
    .sidebar{width:360px;min-width:320px;max-width:420px;min-height:0;border-right:1px solid var(--border);background:var(--panel);display:flex;flex-direction:column}
    .sbTop{padding:14px;border-bottom:1px solid var(--border);display:flex;flex-direction:column;gap:10px}
    app-contact-list{display:block;flex:1;min-height:0}
    .search{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:14px;background:var(--input);border:1px solid var(--border)}
    .search input{width:100%;border:0;outline:0;background:transparent;color:var(--text);font-size:14px}
    .sIcon{opacity:.8}
    .quickFilters{display:flex;gap:8px;flex-wrap:wrap}
    .chip{
      border:1px solid var(--border);
      background:transparent;
      color:var(--muted);
      border-radius:999px;
      padding:4px 10px;
      font-size:12px;
      cursor:pointer;
      display:inline-flex;
      align-items:center;
      gap:6px;
    }
    .chip:hover{color:var(--text);border-color:rgba(0,168,132,.35);background:rgba(0,168,132,.08)}
    .chip.active{color:var(--text);border-color:rgba(0,168,132,.45);background:rgba(0,168,132,.15)}
    .chip.lead.active{border-color:rgba(22,163,74,.45);background:rgba(22,163,74,.15);color:rgb(134,239,172)}
    .chip.sales.active{border-color:rgba(37,99,235,.45);background:rgba(37,99,235,.15);color:rgb(147,197,253)}
    .chip.humanRequired.active{border-color:rgba(217,119,6,.45);background:rgba(217,119,6,.16);color:rgb(253,186,116)}
    :host-context(body.light) .chip{color:#334155}
    :host-context(body.light) .chip:hover{color:#0f172a;background:rgba(2,132,199,.08);border-color:rgba(2,132,199,.22)}
    :host-context(body.light) .chip.active{color:#0f172a;background:rgba(2,132,199,.14);border-color:rgba(2,132,199,.30)}
    :host-context(body.light) .chip.lead.active{color:rgb(22,101,52);background:rgba(34,197,94,.22);border-color:rgba(22,163,74,.45)}
    :host-context(body.light) .chip.sales.active{color:rgb(30,64,175);background:rgba(59,130,246,.20);border-color:rgba(37,99,235,.45)}
    :host-context(body.light) .chip.humanRequired.active{color:rgb(154,52,18);background:rgba(251,146,60,.22);border-color:rgba(217,119,6,.45)}
    .count{font-weight:800;font-size:11px;opacity:.95}
    .chat{
      flex:1;
      min-width:0;
      min-height:0;
      display:grid;
      grid-template-rows:auto minmax(0,1fr) auto;
    }
    .sidebar.hidden{display:none}
    @media (max-width: 900px){
      .sidebar{width:100%;max-width:none}
      .chat{width:100%}
    }
  `]
})
export class InboxComponent {
  mobileChatOpen = false;
  chatSearchQuery = '';

  constructor(public store: ChatStoreService) {}

  onSelect(id: string) {
    this.store.selectedConversationId.set(id);
    this.chatSearchQuery = '';
    if (window.matchMedia('(max-width: 900px)').matches) this.mobileChatOpen = true;
  }

  onBack() {
    if (window.matchMedia('(max-width: 900px)').matches) this.mobileChatOpen = false;
  }

  onToggleBot(enabled: boolean) {
    this.store.setBotEnabled(this.store.selectedConversationId(), enabled);
  }

  onToggleHumanRequired(required: boolean) {
    this.store.setHumanRequired(this.store.selectedConversationId(), required);
  }

  async onHeaderAction(actionId: string): Promise<void> {
    if (actionId !== 'enable-bot') return;
    await this.store.enableBotViaDashboard(this.store.selectedConversationId());
  }

  onChatSearch(query: string): void {
    this.chatSearchQuery = query.trim();
  }
}
